/**
 * Composable для работы с WebSocket сигналингом
 * — Типобезопасная отправка/приём
 * — Авто-rejoin после реконнекта
 * — Backoff с джиттером
 * — Корректная пауза/очистка таймеров
 */

import { ref, type Ref } from 'vue';
import type {
  SignalingMessage,
  JoinedPayload,
  PeersPayload,
  PeerJoinedPayload,
  PeerReconnectedPayload,
  PeerReadyPayload,
  OfferSendPayload,
  OfferAcceptPayload,
  AnswerSendPayload,
  AnswerAcceptPayload,
  IceCandidatePayload,
  MetricsPayload,
  PeerLeftPayload,
  PeerMetricsPayload,
  ErrorPayload
} from '../types';
import { EventHandlerManager, type UnsubscribeFn, calculateExponentialBackoff } from '../utils/eventHandlers';
import {
  WS_OPEN_TIMEOUT,
  MAX_RECONNECT_ATTEMPTS,
  PING_INTERVAL,
  PONG_TIMEOUT,
  MAX_OUTBOX_SIZE
} from '../constants';

/** Входящие события от сервера (как у тебя) */
interface SignalingEventMap {
  joined: JoinedPayload;
  peers: PeersPayload;
  'peer-joined': PeerJoinedPayload;
  'peer-reconnected': PeerReconnectedPayload;
  'peer-ready': PeerReadyPayload;
  offer: OfferAcceptPayload;
  answer: AnswerAcceptPayload;
  'ice-candidate': IceCandidatePayload;
  'peer-left': PeerLeftPayload;
  'peer-metrics': PeerMetricsPayload;
  error: ErrorPayload;
}

/** Исходящие события (что мы отправляем на сервер) — типобезопасно */
interface OutboundEventMap {
  join: { roomId: string; token: string; displayName: string };
  ready: Record<string, never>;
  offer: OfferSendPayload;
  answer: AnswerSendPayload;
  'ice-candidate': { candidate: RTCIceCandidateInit; targetPeerId: string };
  metrics: MetricsPayload;
  leave: Record<string, never>;
  ping: Record<string, never>;
}

/** Универсальная «проводная» форма сообщения */
type WireMessage<K extends string = string, P = unknown> = { type: K; payload: P };

export function useSignaling() {
  // ==================== СОСТОЯНИЕ ====================

  /** WebSocket соединение */
  const ws: Ref<WebSocket | null> = ref(null);

  /** Статус подключения к серверу */
  const connected: Ref<boolean> = ref(false);
  const connecting: Ref<boolean> = ref(false);        // NEW
  const peerId: Ref<string | null> = ref(null);

  /** Флаг процесса переподключения */
  const isReconnecting: Ref<boolean> = ref(false);

  /** Чтобы не реконнектиться после ручного закрытия */
  let manualClose = false;                             // NEW

  /** Буфер исходящих (до сериализации) */
  const outbox: Array<WireMessage<keyof OutboundEventMap, any>> = [];

  /** Сохраняем последние параметры join для авто-rejoin */
  let lastJoinParams: OutboundEventMap['join'] | null = null;  // NEW

  // ==================== МЕНЕДЖЕРЫ ====================

  /** Менеджер обработчиков событий */
  const eventManager = new EventHandlerManager<SignalingEventMap>();

  // ==================== ТАЙМЕРЫ И СЧЕТЧИКИ ====================

  /** Счетчик попыток переподключения */
  let reconnectAttempts = 0;

  /** Таймер для планирования переподключения */
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Интервал отправки ping сообщений */
  let pingInterval: ReturnType<typeof setInterval> | null = null;

  /** Таймер ожидания pong ответа */
  let pongTimeout: ReturnType<typeof setTimeout> | null = null;

  // ==================== УТИЛИТЫ ====================

  /** Джиттер поверх backoff (0.8x…1.2x) */
  function withJitter(ms: number): number {
    const f = 0.8 + Math.random() * 0.4;
    return Math.round(ms * f);
  }

  /** Получить URL WS (через env, fallback — тот же хост) */
  function getWsUrl(): string {
    // Vite: VITE_WS_URL=wss://example.com/ws
    const envUrl = (import.meta as any).env?.VITE_WS_URL as string | undefined;
    if (envUrl) return envUrl;

    const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    const proto = isHttps ? 'wss:' : 'ws:';
    const host = typeof window !== 'undefined' ? window.location.host : 'localhost:3000';
    return `${proto}//${host}/ws`;
  }

  // ==================== ОСНОВНЫЕ ФУНКЦИИ ====================

  /**
   * Подключается к WebSocket серверу
   * Определяет URL автоматически (dev/prod)
   *
   * @returns Promise который resolve при успешном подключении
   */
  function connect(): Promise<void> {
    if (connected.value) return Promise.resolve();
    if (connecting.value) return Promise.resolve(); // защита от двойного вызова

    connecting.value = true;
    manualClose = false;

    return new Promise((resolve, reject) => {
      const wsUrl = getWsUrl();
      console.log('🔌 Подключение к WebSocket:', wsUrl);

      const sock = new WebSocket(wsUrl);
      ws.value = sock;

      // Таймаут открытия
      const openTimer = setTimeout(() => {
        try { sock.close(4000, 'Open timeout'); } catch {}
        reject(new Error('WebSocket open timeout'));
      }, WS_OPEN_TIMEOUT);

      sock.onopen = () => {
        clearTimeout(openTimer);
        console.log('✅ WebSocket подключен');
        connected.value = true;
        connecting.value = false;
        isReconnecting.value = false;
        reconnectAttempts = 0;

        // Keep-alive
        startPing();

        // Повторный вход (если ранее делали join)
        if (lastJoinParams) {
          // важно отправить join раньше outbox, чтобы комната восстановилась
          send('join', lastJoinParams);
        }

        // Отправляем очередь
        flushOutbox();

        resolve();
      };

      sock.onclose = (event: CloseEvent) => {
        clearTimeout(openTimer);
        connected.value = false;
        connecting.value = false;
        stopPing();

        console.log('🔌 WebSocket закрыт', event.code, event.reason);

        // Не реконнектимся после ручного закрытия
        if (manualClose) return;

        // Авто-реконнект для «ненормальных» закрытий
        if (event.code !== 1000) scheduleReconnect();
      };

      sock.onerror = (error: Event) => {
        clearTimeout(openTimer);
        connecting.value = false;
        console.error('❌ WebSocket error:', error);
        // Ошибка часто сопровождается onclose → реконнект запустится там.
        // Но если onclose не пришёл — дёрнем сами:
        if (!manualClose && !connected.value) scheduleReconnect();
        reject(error);
      };

      sock.onmessage = (event: MessageEvent) => {
        handleMessage(event.data);
      };
    });
  }

  function handleMessage(data: string): void {
    try {
      const msg = JSON.parse(data) as SignalingMessage | WireMessage;
      // keep-alive
      if ((msg as WireMessage).type === 'pong') {
        handlePong();
        return;
      }

      // joined: сохраняем peerId
      if ((msg as WireMessage<'joined'>).type === 'joined') {
        const payload = (msg as WireMessage<'joined', JoinedPayload>).payload;
        if (payload?.peerId) peerId.value = payload.peerId;
      }

      // Проксируем остальное по типам
      const { type, payload } = msg as WireMessage<keyof SignalingEventMap, any>;
      eventManager.emit(type, payload);
    } catch (e) {
      console.error('❌ Ошибка парсинга сообщения:', e, data);
    }
  }

  /** Типобезопасная отправка */
  function send<K extends keyof OutboundEventMap>(type: K, payload: OutboundEventMap[K]): void {
    const wire: WireMessage<K, OutboundEventMap[K]> = { type, payload };

    const s = ws.value;
    if (s && s.readyState === WebSocket.OPEN) {
      s.send(JSON.stringify(wire));
      console.log('📤 Отправлено:', type, payload);
    } else {
      enqueueMessage(wire);
      console.log('📥 В очередь:', type, payload);
    }
  }

  function enqueueMessage<K extends keyof OutboundEventMap>(wire: WireMessage<K, OutboundEventMap[K]>): void {
    if (outbox.length >= MAX_OUTBOX_SIZE) {
      outbox.shift();
      console.warn('⚠️ Outbox переполнен: удалено самое старое сообщение');
    }
    outbox.push(wire);
  }

  /**
   * Отправляет все сообщения из очереди
   */
  function flushOutbox(): void {
    if (!ws.value || ws.value.readyState !== WebSocket.OPEN) return;
    const toSend = outbox.splice(0, outbox.length);
    for (const msg of toSend) {
      ws.value!.send(JSON.stringify(msg));
    }
  }

  // ==================== API МЕТОДЫ СИГНАЛИНГА ====================

  /**
   * Отправляет запрос на присоединение к комнате
   */
  function join(roomId: string, token: string, displayName: string): void {
    const payload: OutboundEventMap['join'] = { roomId, token, displayName };
    lastJoinParams = payload;               // сохранить для авто-rejoin
    send('join', payload);
  }

  /**
   * Сигнализирует о готовности к установлению P2P соединения
   */
  function sendReady(): void {
    send('ready', {});
  }

  /**
   * Отправляет SDP offer собеседнику
   */
  function sendOffer(sdp: string, targetPeerId: string): void {
    send('offer', { sdp, targetPeerId });
  }

  /**
   * Отправляет SDP answer собеседнику
   */
  function sendAnswer(sdp: string, targetPeerId: string): void {
    send('answer', { sdp, targetPeerId });
  }

  /**
   * Отправляет ICE кандидата собеседнику
   */
  function sendIceCandidate(candidate: RTCIceCandidateInit, targetPeerId: string): void {
    send('ice-candidate', { candidate, targetPeerId });
  }

  /**
   * Отправляет метрики соединения собеседнику
   */
  function sendMetrics(metrics: MetricsPayload): void {
    send('metrics', metrics);
  }

  /**
   * Покидает комнату и закрывает соединение
   */
  function leave(): void {
    try { send('leave', {}); } catch {}

    manualClose = true;                   // больше не реконнектимся
    lastJoinParams = null;                // сбрасываем привязку комнаты

    const s = ws.value;
    if (s) {
      try { s.close(1000, 'User left'); } catch {}
    }

    // Полный сброс
    ws.value = null;
    connected.value = false;
    connecting.value = false;
    peerId.value = null;
    outbox.length = 0;
    stopReconnect();
    stopPing();
  }

  // ==================== ПЕРЕПОДКЛЮЧЕНИЕ ====================

  /**
   * Планирует попытку переподключения с экспоненциальной задержкой
   */
  function scheduleReconnect(): void {
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('❌ Достигнут лимит попыток реконнекта');
      return;
    }
    if (reconnectTimeout) clearTimeout(reconnectTimeout);

    isReconnecting.value = true;
    reconnectAttempts++;

    const base = calculateExponentialBackoff(reconnectAttempts);
    const delay = withJitter(base);

    console.log(`🔄 Реконнект ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} через ${delay}мс`);

    reconnectTimeout = setTimeout(async () => {
      try {
        await connect();
        console.log('✅ Переподключение успешно');
      } catch (err) {
        console.error('❌ Не удалось переподключиться:', err);
        scheduleReconnect();
      }
    }, delay);
  }

  /**
   * Останавливает процесс переподключения
   */
  function stopReconnect(): void {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    isReconnecting.value = false;
    reconnectAttempts = 0;
  }

  // ==================== KEEP-ALIVE ====================

  /**
   * Запускает периодическую отправку ping сообщений
   * для поддержания соединения активным
   */
  function startPing(): void {
    if (pingInterval) return;

    pingInterval = setInterval(() => {
      if (!ws.value || ws.value.readyState !== WebSocket.OPEN) return;

      send('ping', {}); // идёт в очередь/сразу — типобезопасно

      if (pongTimeout) clearTimeout(pongTimeout);
      pongTimeout = setTimeout(() => {
        console.warn('⚠️ PONG timeout → закрываем и реконнектимся');
        try { ws.value?.close(); } catch {}
      }, PONG_TIMEOUT);
    }, PING_INTERVAL);
  }

  /**
   * Обрабатывает получение pong ответа
   */
  function handlePong(): void {
    if (pongTimeout) {
      clearTimeout(pongTimeout);
      pongTimeout = null;
    }
  }

  /**
   * Останавливает механизм keep-alive
   */
  function stopPing(): void {
    if (pingInterval) {
      clearInterval(pingInterval);
      pingInterval = null;
    }
    if (pongTimeout) {
      clearTimeout(pongTimeout);
      pongTimeout = null;
    }
  }

  // ==================== РЕГИСТРАЦИЯ ОБРАБОТЧИКОВ ====================

  /**
   * Регистрирует обработчик события "присоединение к комнате"
   */
  function onJoined(handler: (payload: JoinedPayload) => void): UnsubscribeFn {
    return eventManager.on('joined', handler);
  }

  /**
   * Регистрирует обработчик события "список существующих пиров"
   */
  function onPeers(handler: (payload: PeersPayload) => void): UnsubscribeFn {
    return eventManager.on('peers', handler);
  }

  /**
   * Регистрирует обработчик события "новый пир присоединился"
   */
  function onPeerJoined(handler: (payload: PeerJoinedPayload) => void): UnsubscribeFn {
    return eventManager.on('peer-joined', handler);
  }

  /**
   * Регистрирует обработчик события "пир переподключился"
   */
  function onPeerReconnected(handler: (payload: PeerReconnectedPayload) => void): UnsubscribeFn {
    return eventManager.on('peer-reconnected', handler);
  }

  /**
   * Регистрирует обработчик события "пир готов к соединению"
   */
  function onPeerReady(handler: (payload: PeerReadyPayload) => void): UnsubscribeFn {
    return eventManager.on('peer-ready', handler);
  }

  /**
   * Регистрирует обработчик события "получен SDP offer"
   */
  function onOffer(handler: (payload: OfferAcceptPayload) => void): UnsubscribeFn {
    return eventManager.on('offer', handler);
  }

  /**
   * Регистрирует обработчик события "получен SDP answer"
   */
  function onAnswer(handler: (payload: AnswerAcceptPayload) => void): UnsubscribeFn {
    return eventManager.on('answer', handler);
  }

  /**
   * Регистрирует обработчик события "получен ICE кандидат"
   */
  function onIceCandidate(handler: (payload: IceCandidatePayload) => void): UnsubscribeFn {
    return eventManager.on('ice-candidate', handler);
  }

  /**
   * Регистрирует обработчик события "пир покинул комнату"
   */
  function onPeerLeft(handler: (payload: PeerLeftPayload) => void): UnsubscribeFn {
    return eventManager.on('peer-left', handler);
  }

  /**
   * Регистрирует обработчик события "получены метрики от пира"
   */
  function onPeerMetrics(handler: (payload: PeerMetricsPayload) => void): UnsubscribeFn {
    return eventManager.on('peer-metrics', handler);
  }

  /**
   * Регистрирует обработчик события "ошибка"
   */
  function onError(handler: (payload: ErrorPayload) => void): UnsubscribeFn {
    return eventManager.on('error', handler);
  }

  // ==================== ВОЗВРАТ ====================

  return {
    // Состояние
    connected,
    connecting,     // NEW
    peerId,
    isReconnecting,

    // Управление соединением
    connect,
    leave,
    stopReconnect,

    // API
    join,
    sendReady,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
    sendMetrics,

    // Handlers
    onJoined,
    onPeers,
    onPeerJoined,
    onPeerReconnected,
    onPeerReady,
    onOffer,
    onAnswer,
    onIceCandidate,
    onPeerLeft,
    onPeerMetrics,
    onError
  };
}
